// Reveal deferred imagery without delaying requests, changing layout or polling scroll.
const motion = matchMedia('(prefers-reduced-motion: reduce)');
const selector = 'img[loading="lazy"], .fit-guide-card, reviews-ticker-fixed';
let stop = () => {};

function start() {
  stop();
  const main = document.querySelector('main');
  if (!main || motion.matches || !('IntersectionObserver' in window) || !Element.prototype.animate) return;
  const lifecycle = new AbortController();
  const seen = new WeakSet();
  const pending = new Map();
  const animations = new Set();
  let initial = true;

  function restore(element) {
    const state = pending.get(element);
    if (!state) return;
    if (state.opacity) element.style.setProperty('opacity', state.opacity, state.priority);
    else element.style.removeProperty('opacity');
    pending.delete(element);
  }

  async function reveal(element, delay) {
    const state = pending.get(element);
    if (!state || state.started) return;
    state.started = true;
    if (element instanceof HTMLImageElement) {
      if (!element.complete) await new Promise(resolve => {
        element.addEventListener('load', resolve, {once: true, signal: lifecycle.signal});
        element.addEventListener('error', resolve, {once: true, signal: lifecycle.signal});
        lifecycle.signal.addEventListener('abort', resolve, {once: true});
      });
      if (lifecycle.signal.aborted) return;
      if (!element.naturalWidth) { restore(element); return; }
      try { await element.decode(); } catch (_) { /* A changed source remains usable. */ }
    }
    if (lifecycle.signal.aborted) return;
    restore(element);
    if (!element.isConnected || motion.matches) return;
    const timing = {duration: 620, delay, easing: 'cubic-bezier(.22,1,.36,1)', fill: 'backwards'};
    // Add the entrance offset to the authored position (including responsive
    // garment centering). Keep opacity separate so it fades rather than adds.
    const effects = [
      element.animate([{opacity: 0}, {opacity: state.targetOpacity}], timing),
      element.animate([{translate: '0 14px'}, {translate: '0 0'}], {...timing, composite: 'add'})
    ];
    for (const animation of effects) {
      animations.add(animation);
      animation.finished.catch(() => {}).finally(() => animations.delete(animation));
    }
  }

  const observer = new IntersectionObserver(entries => {
    let order = 0;
    for (const entry of entries) {
      if (!entry.isIntersecting) continue;
      observer.unobserve(entry.target);
      reveal(entry.target, Math.min(order++ * 45, 90));
    }
  }, {threshold: 0.01});

  function register(element) {
    if (seen.has(element) || element.closest('dialog, [role="dialog"], .fit-guide-card__icon, [aria-hidden="true"]')) return;
    const rect = element.getBoundingClientRect();
    if (rect.width < 100 || rect.height < 80) return;
    seen.add(element);
    // Preserve the initial viewport and LCP, including direct links into the grid.
    if (initial && rect.top < innerHeight && rect.bottom > 0 && rect.left < innerWidth && rect.right > 0) return;
    const targetOpacity = getComputedStyle(element).opacity;
    if (Number(targetOpacity) === 0) return;
    pending.set(element, {opacity: element.style.getPropertyValue('opacity'), priority: element.style.getPropertyPriority('opacity'), targetOpacity});
    element.style.opacity = '0';
    observer.observe(element);
  }

  function scan(root) {
    if (!(root instanceof Element)) return;
    if (root.matches(selector)) register(root);
    root.querySelectorAll(selector).forEach(register);
  }
  scan(main);
  initial = false;
  const mutations = new MutationObserver(records => {
    for (const record of records) record.addedNodes.forEach(scan);
    for (const element of pending.keys()) {
      if (!element.isConnected) { observer.unobserve(element); restore(element); }
    }
  });
  mutations.observe(main, {childList: true, subtree: true});
  stop = () => {
    lifecycle.abort(); observer.disconnect(); mutations.disconnect();
    animations.forEach(animation => animation.cancel());
    for (const element of pending.keys()) restore(element);
  };
}
start();
motion.addEventListener('change', start);
window.addEventListener('pagehide', () => stop());
window.addEventListener('pageshow', event => { if (event.persisted) start(); });
